import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, descargarExcel } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, KeyRound, Trash2, Mail, Phone, Download } from "lucide-react";

const empty = { nombre: "", apellidos: "", nif: "", telefono: "", email: "", direccion: "", ciudad: "", codigo_postal: "" };

export default function Padres() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(null);
  const [pwd, setPwd] = useState("");
  const [form, setForm] = useState({ ...empty, password: "" });
  const [editId, setEditId] = useState(null);
  const [alumnos, setAlumnos] = useState([]);
  const [hijos, setHijos] = useState({});
  const [hijosSel, setHijosSel] = useState([]);

  const load = () => api.get("/padres").then(async ({ data }) => {
    setItems(data);
    const mapa = {};
    await Promise.all(data.map(async (p) => {
      const { data: h } = await api.get(`/padres/${p.id}/hijos`);
      mapa[p.id] = h;
    }));
    setHijos(mapa);
  });
  useEffect(() => {
    load();
    api.get("/alumnos").then(({ data }) => setAlumnos(data)).catch(() => setAlumnos([]));
  }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const abrir = (p) => {
    setForm(p ? { ...empty, ...p, password: "" } : { ...empty, password: "" });
    setEditId(p?.id || null);
    setHijosSel(p ? (hijos[p.id] || []).map((h) => h.id) : []);
    setOpen(true);
  };

  const save = async () => {
    try {
      let id = editId;
      if (editId) {
        const { password, ...rest } = form;
        await api.put(`/padres/${editId}`, rest);
      } else {
        id = (await api.post("/padres", form)).data.id;
      }
      await api.post(`/padres/${id}/hijos`, { alumno_ids: hijosSel });
      toast.success(editId ? "Datos actualizados" : "Padre/tutor creado");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const savePwd = async () => {
    try {
      await api.post(`/padres/${pwdOpen}/password`, { password: pwd });
      toast.success("Contraseña actualizada");
      setPwdOpen(null);
      setPwd("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/padres/${id}`);
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="padres-page">
      <PageHeader
        title="Padres y tutores"
        subtitle="Datos de contacto, NIF/CIF y dirección de facturación"
        action={
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-full font-semibold" data-testid="export-pagos-btn"
              onClick={() => descargarExcel("/reportes/pagos", "pagos.xlsx")}>
              <Download className="h-4 w-4 mr-2" /> Excel de pagos
            </Button>
            <Button className="rounded-full font-semibold" data-testid="add-padre-btn"
              onClick={() => abrir(null)}>
              <Plus className="h-4 w-4 mr-2" /> Nuevo padre/tutor
            </Button>
          </div>
        }
      />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {items.map((p) => (
          <Card key={p.id} className="rounded-2xl p-6 border-border shadow-sm" data-testid={`padre-card-${p.id}`}>
            <h3 className="font-display font-bold text-lg leading-tight">{p.nombre} {p.apellidos}</h3>
            <div className="mt-3 space-y-1.5 text-sm text-muted-foreground">
              <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> {p.email}</p>
              {p.telefono && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {p.telefono}</p>}
              {p.nif && <p>NIF/CIF: {p.nif}</p>}
              {p.direccion && <p>{p.direccion}{p.ciudad ? `, ${p.ciudad}` : ""} {p.codigo_postal}</p>}
            </div>
            {(hijos[p.id] || []).length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2" data-testid={`hijos-de-${p.id}`}>
                {hijos[p.id].map((h) => (
                  <a key={h.id} href={`/admin/alumnos/${h.id}`} data-testid={`hijo-${h.id}`}
                    className="px-3 py-1.5 rounded-full text-xs font-semibold border border-border hover:bg-accent transition-colors duration-200">
                    {h.nombre} · {h.matricula}
                  </a>
                ))}
              </div>
            )}
            <div className="flex gap-2 mt-6">
              <Button variant="outline" size="sm" className="rounded-lg" data-testid={`edit-padre-${p.id}`}
                onClick={() => abrir(p)}>
                <Pencil className="h-3.5 w-3.5 mr-1.5" /> Editar
              </Button>
              <Button variant="outline" size="sm" className="rounded-lg" data-testid={`pwd-padre-${p.id}`}
                onClick={() => setPwdOpen(p.id)}>
                <KeyRound className="h-3.5 w-3.5" />
              </Button>
              <Button variant="outline" size="sm" className="rounded-lg text-destructive" data-testid={`delete-padre-${p.id}`}
                onClick={() => remove(p.id)}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </Card>
        ))}
        {items.length === 0 && <p className="text-muted-foreground text-sm">Todavía no hay padres registrados.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="padre-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editId ? "Editar padre/tutor" : "Nuevo padre/tutor"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Nombre</Label>
                <Input value={form.nombre} onChange={(e) => set("nombre", e.target.value)} data-testid="padre-nombre-input" />
              </div>
              <div className="grid gap-2">
                <Label>Apellidos</Label>
                <Input value={form.apellidos} onChange={(e) => set("apellidos", e.target.value)} data-testid="padre-apellidos-input" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>NIF / CIF</Label>
                <Input value={form.nif} onChange={(e) => set("nif", e.target.value)} data-testid="padre-nif-input" />
              </div>
              <div className="grid gap-2">
                <Label>Teléfono</Label>
                <Input value={form.telefono} onChange={(e) => set("telefono", e.target.value)} data-testid="padre-telefono-input" />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="padre-email-input" />
            </div>
            {!editId && (
              <div className="grid gap-2">
                <Label>Contraseña del portal (mín. 6)</Label>
                <PasswordInput value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="padre-password-input" />
              </div>
            )}
            <div className="grid gap-2">
              <Label>Dirección</Label>
              <Input value={form.direccion} onChange={(e) => set("direccion", e.target.value)} data-testid="padre-direccion-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Ciudad</Label>
                <Input value={form.ciudad} onChange={(e) => set("ciudad", e.target.value)} data-testid="padre-ciudad-input" />
              </div>
              <div className="grid gap-2">
                <Label>Código postal</Label>
                <Input value={form.codigo_postal} onChange={(e) => set("codigo_postal", e.target.value)} data-testid="padre-cp-input" />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Hijos/as vinculados</Label>
              <div className="max-h-48 overflow-y-auto rounded-xl border border-border divide-y divide-border"
                data-testid="padre-hijos-lista">
                {alumnos
                  .filter((a) => !a.padre_id || a.padre_id === editId)
                  .map((a) => (
                    <button key={a.id} type="button" data-testid={`padre-hijo-${a.id}`}
                      onClick={() => setHijosSel(hijosSel.includes(a.id)
                        ? hijosSel.filter((x) => x !== a.id) : [...hijosSel, a.id])}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors duration-200 ${
                        hijosSel.includes(a.id) ? "bg-accent font-semibold" : "hover:bg-muted"}`}>
                      {a.nombre} {a.apellidos}
                      <span className="block text-xs text-muted-foreground">{a.matricula}</span>
                    </button>
                  ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Sólo aparecen los alumnos sin tutor asignado y los que ya son de esta familia.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-full font-semibold" onClick={save} data-testid="save-padre-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwdOpen} onOpenChange={(v) => !v && setPwdOpen(null)}>
        <DialogContent className="rounded-2xl" data-testid="padre-pwd-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Nueva contraseña</DialogTitle></DialogHeader>
          <div className="grid gap-2">
            <Label>Contraseña (mín. 6)</Label>
            <PasswordInput value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="padre-newpwd-input" />
          </div>
          <DialogFooter>
            <Button className="rounded-full font-semibold" onClick={savePwd} data-testid="save-padre-pwd-btn">Actualizar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
